import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { collection, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { renderTextWithLinks, formatRelative } from '../utils/notesHelpers';

/* ═══════════════════════════════════════════════════
   QuickNotesPanel — right-side panel dalam Workspace
   Papar HANYA nota yang isQuickNote === true.
   Read-mostly: tap link, salin baris, toggle off quick.
   Untuk edit nota — pergi page Notes.
   ═══════════════════════════════════════════════════ */

const QuickNotesPanel = ({ isOpen, onClose, hideBackdrop = false }) => {
    const [notes, setNotes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [copiedKey, setCopiedKey] = useState(null);

    // Real-time load (sentiasa listen — data nota kecil, kos diabaikan)
    useEffect(() => {
        if (!auth.currentUser) { setIsLoading(false); return; }
        const notesRef = collection(db, 'users', auth.currentUser.uid, 'notes');
        const unsub = onSnapshot(
            notesRef,
            (snap) => {
                const items = snap.docs
                    .map((d) => ({ id: d.id, ...d.data() }))
                    .filter((n) => n.isQuickNote)
                    .sort((a, b) => (b.updatedAt || b.createdAt || 0) - (a.updatedAt || a.createdAt || 0));
                setNotes(items);
                setIsLoading(false);
            },
            (err) => {
                console.error('[QuickNotes] load error:', err);
                setIsLoading(false);
            }
        );
        return () => unsub();
    }, []);

    const copyLine = async (value, key) => {
        try {
            await navigator.clipboard.writeText(value);
            setCopiedKey(key);
            setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1500);
        } catch (err) {
            console.error('[QuickNotes] copy failed:', err);
        }
    };

    const copyBtn = (value, key) => (
        <button
            onClick={() => copyLine(value, key)}
            title="Salin baris ini"
            className="shrink-0 p-1 rounded-md text-slate-400 hover:text-indigo-600 hover:bg-slate-100 dark:hover:bg-slate-700 active:scale-95 transition-all"
        >
            <span className="material-symbols-outlined text-[16px]">{copiedKey === key ? 'check' : 'content_copy'}</span>
        </button>
    );

    const removeFromQuick = async (id) => {
        if (!auth.currentUser) return;
        try {
            await updateDoc(doc(db, 'users', auth.currentUser.uid, 'notes', id), {
                isQuickNote: false,
                updatedAt: Date.now(),
            });
        } catch (err) {
            console.error('[QuickNotes] remove failed:', err);
            alert('Gagal kemaskini. Sila cuba lagi.');
        }
    };

    return (
        <>
            {/* Backdrop (fade) — disembunyikan bila dibuka dari dalam modal supaya modal tetap interactive */}
            {!hideBackdrop && (
                <div
                    onClick={onClose}
                    className={`fixed inset-0 bg-black/30 backdrop-blur-sm z-[10050] transition-opacity duration-300 ${
                        isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
                    }`}
                />
            )}

            {/* Panel (slide-in dari kanan) */}
            <aside
                style={{
                    transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
                    transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
                }}
                className="fixed top-0 right-0 bottom-0 z-[10060] w-full sm:w-[360px] bg-white dark:bg-slate-900 shadow-2xl flex flex-col"
            >
                {/* Header */}
                <div className="shrink-0 flex items-center gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-800">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white shrink-0">
                        <span className="material-symbols-outlined text-[18px]" style={{ fontVariationSettings: "'FILL' 1" }}>sticky_note_2</span>
                    </div>
                    <div className="flex-1 min-w-0">
                        <h2 className="text-base font-bold text-slate-800 dark:text-white">Quick Notes</h2>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{notes.length} nota</p>
                    </div>
                    <button
                        onClick={onClose}
                        aria-label="Tutup"
                        className="w-9 h-9 flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    >
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto custom-scrollbar p-3">
                    {isLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="w-6 h-6 border-2 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin"></div>
                        </div>
                    ) : notes.length === 0 ? (
                        <div className="text-center py-16 px-4">
                            <span className="material-symbols-outlined text-[40px] text-slate-300 dark:text-slate-700">bolt</span>
                            <p className="mt-3 text-sm text-slate-600 dark:text-slate-300 font-medium">Belum ada Quick Notes</p>
                            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                Pergi ke page <strong>Notes</strong>, tekan ikon <span className="inline-flex items-baseline align-middle"><span className="material-symbols-outlined text-[14px] text-indigo-500" style={{ fontVariationSettings: "'FILL' 1" }}>bolt</span></span> pada nota untuk muncul di sini.
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {notes.map((note) => (
                                <div
                                    key={note.id}
                                    className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl p-3"
                                >
                                    {note.title && (
                                        <div className="flex items-start gap-2 mb-1">
                                            <h3 className="flex-1 min-w-0 text-sm font-bold text-slate-800 dark:text-white break-words">
                                                {note.title}
                                            </h3>
                                            {copyBtn(note.title, `${note.id}:title`)}
                                        </div>
                                    )}
                                    {note.text && note.text.split('\n').map((line, i) => (
                                        line.trim() ? (
                                            <div key={i} className="flex items-start gap-2">
                                                <div className="flex-1 min-w-0 text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap break-words leading-relaxed">
                                                    {renderTextWithLinks(line)}
                                                </div>
                                                {copyBtn(line, `${note.id}:${i}`)}
                                            </div>
                                        ) : (
                                            <div key={i} className="h-1.5" />
                                        )
                                    ))}
                                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-200/70 dark:border-slate-700/70">
                                        <span className="text-[11px] text-slate-400">
                                            {formatRelative(note.updatedAt || note.createdAt)}
                                        </span>
                                        <button
                                            onClick={() => removeFromQuick(note.id)}
                                            title="Buang dari Quick Notes"
                                            className="p-1 rounded-md text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 active:scale-95 transition-all"
                                        >
                                            <span
                                                className="material-symbols-outlined text-[16px]"
                                                style={{ fontVariationSettings: "'FILL' 1" }}
                                            >
                                                bolt
                                            </span>
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </aside>
        </>
    );
};

export default QuickNotesPanel;
